import { useState, useMemo, useCallback, useEffect } from "react";
import { Button, Chip, Stack, Switch, Tooltip } from "@mui/material";

import {
  Box,
  Pagination,
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TableSortLabel,
  Checkbox,
  Typography,
  PaginationItem,
  Link,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchBarContainer from "../../ui-component/SearchBarContainer";
import { makeStyles, useTheme } from "@mui/styles";
import Header from "./Header";

const useStyles = makeStyles((theme) => ({
  tableCell: {
    backgroundColor: "transparent",
    fontSize: "12px !important",
    color: `${theme.palette.text.color} !important`,
    height: "10px",
    padding: "6px 4px !important",
    borderBottom: `1px solid ${theme.palette.border.color} !important`,
  },
  headerCell: {
    backgroundColor: theme.palette.mode === "dark" ? "#171A1E" : "#F2F5F9",
    border: "none",
    fontSize: "14px",
    color: `${theme.palette.text.color} !important`,
    height: "10px",
    padding: "6px 4px !important",
  },
}));

// `theme` is passed in (not read via useTheme here): this function is called
// once per table cell, so calling a hook inside it would make the hook count
// vary with row/column count — a Rules-of-Hooks violation. The caller already
// holds the theme.
const RenderColumnItem = (column, row, object, handleUpdateAnnotation, theme) => {
  if (column.tableRenderType === "toggleButton") {
    const currentStatus = row.annotationStatus;
    const isAddedToWMS = currentStatus === "ADDED TO WMS";
    const buttonText = isAddedToWMS ? "Added" : "Add to WMS";
    const buttonColor = isAddedToWMS ? "#2F2F2F" : "#006bce";

    return (
      <button
        // disabled={buttonText === 'Added'}
        onClick={(e) => {
          e.stopPropagation();
          const newStatus = isAddedToWMS ? "REMOVED FROM WMS" : "ADDED TO WMS";
          handleUpdateAnnotation({ ...row, annotationStatus: newStatus });
        }}
        style={{
          backgroundColor: buttonColor,
          border: `1px solid ${buttonColor}`,
          color: "#fff",
          fontSize: "10px",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: "4px",
          transition: "all 0.2s ease-in-out",
          minWidth: "80px",
          textAlign: "center",
          display: "block",
          margin: "0 auto",
        }}
      >
        {buttonText}
      </button>
    );
  }

  switch (column.type) {
    case "switch":
      return (
        <Switch
          size="small"
          checked={!!column.value}
          disabled={column.isDisabled?.(row)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => column.onChange?.(row, e.target.checked)}
        />
      );
    case "textfield":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.id === "locationBoxCount"
            ? column.value || 0
            : column.value || "N/A"}
        </Typography>
      );
    case "description":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.value || "N/A"}
        </Typography>
      );
    case "checkbox":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.value?.join(", ") || "N/A"}
        </Typography>
      );
    case "radioSelect":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.value || "N/A"}
        </Typography>
      );

    case "form":
      return (
        <Typography
          onClick={(e) => {
            e.stopPropagation();
            if (column.onClick) {
              column.onClick(row);
            }
          }}
          sx={{
            fontSize: "14px !important",
            marginLeft: "20px",
            color: "#006BCE",
            textDecoration: "underline",
            cursor: "pointer",
            "&:hover": {
              color: "#004a8f",
            },
          }}
        >
          {"Form"}
        </Typography>
      );
    case "url":
      return (
        <Link
          href={column.value || "#"}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "14px !important",
            textDecoration: "underline",
            color: column.value ? "#006BCE" : "inherit",
            cursor: column.value ? "pointer" : "default",
            pointerEvents: !column.value && "none",
          }}
        >
          {column.value || "N/A"}
        </Link>
      );
    case "icon":
      return (
        <column.icon
          sx={{
            color: "#fff",
            fontSize: "14px",
            "&:hover": {
              color: "#fff",
            },
          }}
        />
      );
    case "button":
      return (
        <button
          onClick={column.onClick}
          style={{
            backgroundColor: "transparent",
            border: "none",
            color: "#fff",
            fontSize: "14px",
            cursor: "pointer",
            padding: "0",
            margin: "0",
            "&:hover": {
              color: "#fff",
            },
          }}
        >
          {column.label}
        </button>
      );
    case "link":
      return (
        <a
          href
          onClick={column.onClick}
          style={{
            color: "#fff",
            fontSize: "14px",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {column.label}
        </a>
      );
    case "image":
      return (
        <img
          src={column.src}
          alt={column.alt}
          style={{
            width: column.width || "50px",
            height: column.height || "50px",
            objectFit: column.objectFit || "cover",
            borderRadius: column.borderRadius || "4px",
            cursor: column.cursor || "pointer",
            "&:hover": {
              transform: column.transform || "scale(1.05)",
            },
          }}
        />
      );
    case "dropdown":
      if (
        Array.isArray(column.value) &&
        column?.value?.length === 1 &&
        (column?.value[0] === "" || column?.value[0] === "")
      ) {
        return (
          <Typography
            sx={{
              fontSize: "14px !important",
            }}
          >
            N/A
          </Typography>
        );
      }
      if (
        Array.isArray(column.value) &&
        column?.value?.length > 0 &&
        column?.value[0] !== ""
      ) {
        const displayValues = column.value.map((val) => {
          const option = column.options?.find((opt) => opt.value === val);
          return option ? option.label : val;
        });

        const limit = 1;
        const visibleItems = displayValues.slice(0, limit);
        const hiddenCount = displayValues.length - limit;

        const tooltipBgColor =
          theme.palette.mode === "dark" ? "#fff" : "#2F2F2F";
        const tooltipTextColor =
          theme.palette.mode === "dark" ? "#000" : "#fff";

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            {visibleItems.map((item, idx) => (
              <Chip
                key={idx}
                label={item}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "14px",
                  borderColor: theme.palette.border.color || "#ccc",
                  paddingY: "3px",
                }}
              />
            ))}
            {hiddenCount > 0 && (
              <Tooltip
                title={displayValues.slice(limit).join(", ")}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: tooltipBgColor,
                      color: tooltipTextColor,
                      fontSize: "12px",
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                    },
                  },
                  arrow: {
                    sx: {
                      color: tooltipBgColor,
                    },
                  },
                }}
              >
                <Chip
                  label={`+${hiddenCount}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "10px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333" : "#eee",
                    color: theme.palette.text.primary,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        );
      }

      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.id === "locationType"
            ? column.value === "EMPTY BIN"
              ? "Rack"
              : column.value
            : column.id === "locationStatus"
              ? column.value === "PENDING"
                ? "EMPTY"
                : column.value
              : column.value || "N/A"}
        </Typography>
      );
    // const option = column.options?.find(opt => opt.value === column.value);
    // return (
    //   <Typography
    //     sx={{
    //       fontSize: "14px !important",
    //     }}
    //   >
    //     {column.id === 'locationType' ? column.value === 'EMPTY BIN' ? 'Rack' : column.value :
    //       column.id === 'locationStatus' ? column.value === 'PENDING' ? 'EMPTY' : column.value : column.value || 'N/A'}
    //   </Typography>
    // );

    case "tableDropdown":
      const currentStatus = row.annotationStatus;
      const isAddedToWMS = currentStatus === "ADD TO WMS";
      const buttonText = isAddedToWMS ? "REMOVE FROM WMS" : "ADD TO WMS";
      const buttonColor = isAddedToWMS ? "#dc3545" : "#006bce";

      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const newStatus = isAddedToWMS ? "REMOVE FROM WMS" : "ADD TO WMS";
            handleUpdateAnnotation({ ...row, annotationStatus: newStatus });
          }}
          style={{
            backgroundColor: buttonColor,
            border: `1px solid ${buttonColor}`,
            color: "#fff",
            fontSize: "12px",
            cursor: "pointer",
            padding: "4px 8px",
            borderRadius: "4px",
            transition: "all 0.2s ease-in-out",
            minWidth: "120px",
            textAlign: "center",
            display: "block",
            margin: "0 auto",
          }}
        >
          {buttonText}
        </button>
      );

    case "date":
      const timestamp = Number(column.value);
      const digitLength = String(timestamp)?.length;
      const adjustedTimestamp =
        digitLength <= 10 ? timestamp * 1000 : timestamp;
      const date = new Date(adjustedTimestamp);
      return (
        <Typography
          style={{
            fontSize: "14px",
          }}
        >
          {isNaN(date.getTime())
            ? column.value || "Invalid date"
            : date.toLocaleDateString("en-GB", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
        </Typography>
      );
    case "geoJson":
      const latitude = row[`${object}Latitude`];
      const longitude = row[`${object}Longitude`];
      return (
        <Typography
          component="a"
          // href={`https://www.google.com/maps?q=${row.locationLatitude},${row.locationLongitude}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: "none",
            fontSize: "14px !important",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {latitude && longitude ? `${latitude}, ${longitude}` : "N/A"}
        </Typography>
      );
    case "Array":
      if (
        !Array.isArray(column.value) ||
        column.value.length === 0 ||
        (column.value.length === 1 &&
          (column.value[0] === "" || column.value[0] === null))
      ) {
        return (
          <Typography sx={{ fontSize: "14px !important" }}>N/A</Typography>
        );
      }

      let displayValues = [];

      if (column.id === "locationException") {
        displayValues = column.value
          .filter((item) => {
            const auditVal = item.annotationValue;
            const sysVal = item.locationValue;

            const normalize = (val) => {
              if (Array.isArray(val)) {
                return JSON.stringify(val.slice().sort());
              }
              if (val === null || val === undefined) return "";
              return String(val).trim();
            };

            return normalize(auditVal) !== normalize(sysVal);
          })
          .map((item) => item.field || "Unknown Field");
      } else {
        displayValues = column.value.map((val) => {
          if (typeof val === "object" && val !== null) {
            return val.field || val.label || val.name || JSON.stringify(val);
          }
          const option = column.options?.find((opt) => opt.value === val);
          return option ? option.label : val;
        });
      }

      if (displayValues.length === 0) {
        return <Typography sx={{ fontSize: "14px !important" }}>-</Typography>;
      }

      const limit = 1;
      const visibleItems = displayValues.slice(0, limit);
      const hiddenCount = displayValues.length - limit;

      const tooltipBgColor = theme.palette.mode === "dark" ? "#fff" : "#2F2F2F";
      const tooltipTextColor = theme.palette.mode === "dark" ? "#000" : "#fff";

      return (
        <Stack direction="row" spacing={1} alignItems="center">
          {visibleItems.map((item, idx) => (
            <Chip
              key={idx}
              label={item}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: "14px",
                borderColor: theme.palette.border.color || "#ccc",
                paddingY: "3px",
                maxWidth: "150px",
              }}
            />
          ))}
          {hiddenCount > 0 && (
            <Tooltip
              title={displayValues.slice(limit).join(", ")}
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: tooltipBgColor,
                    color: tooltipTextColor,
                    fontSize: "12px",
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                  },
                },
                arrow: {
                  sx: {
                    color: tooltipBgColor,
                  },
                },
              }}
            >
              <Chip
                label={`+${hiddenCount}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "10px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#333" : "#eee",
                  color: theme.palette.text.primary,
                }}
              />
            </Tooltip>
          )}
        </Stack>
      );
    default:
      return null;
  }
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  const aValue = a[orderBy];
  const bValue = b[orderBy];
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const NewListComponent = (props) => {
  const {
    header = true,
    subtitle = "Register and monitor your hardware · Track device status across your fleet",
    emptyText = "No devices registered yet",
    emptySubtext = "Add your first device by running the install command on your hardware.",
    isLoading,
    isPropertiesLoading,
    Icon,
    objectDefinition,
    title,
    showAddButton,
    addButtonText,
    showOtherButton,
    otherButtonText,
    setShowAddForm,
    showActions,
    actionOptions,
    selectedAction,
    onAddClick,
    onOtherClick,
    onDeleteClick,
    handleUpdateAnnotation,
    selectedObject,
    handleChange,
    onRefresh,
    object,
    columns,
    data,
    setData,
    selectedRows,
    setSelectedRows,
    setShowUpdateForm,
    sortingOrder,
    sortBy,
    rowClick = false,
    additionalFilterOption,
    handleAdditionalFilterClick,
    additionalFilteredStatus,
    setAdditionalFilteredStatus,
    lastEvaluatedKey,
    fetchedPages,
    limit = 200,
    setFetchedPages,
    handleNext = () => {},
    page: externalPage,
    onPageChange,
    totalCount,
    isServerPagination = false,
  } = props;
  const hasData = data && data.length > 0;

  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const rowCount = data?.length;
  const numSelected = selectedRows?.length;
  const classes = useStyles();

  const [internalPage, setInternalPage] = useState(1);
  const page = externalPage !== undefined ? externalPage : internalPage;
  const [totalPages, setTotalPages] = useState(0);
  const [tableData, setTableData] = useState(
    isServerPagination ? data : data?.slice((page - 1) * limit, page * limit),
  );

  const [anchorElOptions, setAnchorElOptions] = useState(null);
  const [anchorElAttributes, setAnchorElAttributes] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElStatus, setAnchorElStatus] = useState(null);
  const [anchorElType, setAnchorElType] = useState(null);
  const openOptions = Boolean(anchorElOptions);
  const openAttributes = Boolean(anchorElAttributes);
  const openFilter = Boolean(anchorElFilter);
  const openStatus = Boolean(anchorElStatus);
  const openType = Boolean(anchorElType);

  const [filteredStatus, setFilteredStatus] = useState(null);
  const [filteredType, setFilteredType] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);

  const [order, setOrder] = useState(sortingOrder ? sortingOrder : "asc");
  const [orderBy, setOrderBy] = useState(
    sortBy ? sortBy : columns?.[0]?.id || "",
  );

  const [visibilityStatus, setVisibilityStatus] = useState(
    columns?.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {}),
  );

  const isSelected = (row) => {
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`],
    );
    const isSelected = selectedIndex !== -1;
    return isSelected;
  };

  const handleAddClick = () => {
    if (objectDefinition) {
      props?.setFormObject?.(objectDefinition);
    } else {
      props?.setFormObject(null);
    }
    setShowAddForm(true);
  };

  const handleModifyClick = () => {
    setShowUpdateForm(true);
  };

  const handleOptionsClick = (event) => {
    setAnchorElOptions(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setAnchorElOptions(null);
    setAnchorElAttributes(null);
  };

  const handleAttributesClick = (event) => {
    setAnchorElAttributes(anchorElOptions);
    setAnchorElOptions(null);
  };

  const handleAttributesClose = () => {
    setAnchorElOptions(anchorElAttributes);
    setAnchorElAttributes(null);
  };

  const handleSettingsClick = () => {
    handleOptionsClose();
  };

  const handleToggle = (columnId) => (event) => {
    setVisibilityStatus((prev) => ({
      ...prev,
      [columnId]: event.target.checked,
    }));
  };

  const handleFilterClick = useCallback((event) => {
    setAnchorElFilter(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorElFilter(null);
  }, []);

  const handleStatusClick = () => {
    setAnchorElStatus(anchorElFilter);
    setAnchorElFilter(null);
  };

  const handleStatusClose = () => {
    setAnchorElFilter(anchorElStatus);
    setAnchorElStatus(null);
  };

  const handleTypeClick = () => {
    setAnchorElType(anchorElFilter);
    setAnchorElFilter(null);
  };

  const handleTypeClose = () => {
    setAnchorElFilter(anchorElType);
    setAnchorElType(null);
  };

  // const handleRefreshWithFilters = useCallback(({ refresh = true, filterBy } = {}) => {
  //   const filtersToUse = filterBy !== undefined ? filterBy : activeFilters;
  //   setActiveFilters(filtersToUse);
  //   handleNext({ refresh, filterBy: filtersToUse });
  // }, [activeFilters, handleNext]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows([...data]);
      return;
    }
    setSelectedRows([]);
  };

  const handleSelected = (event, row, rowIndex) => {
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`],
    );
    let newSelectedRows = [...selectedRows];

    if (selectedIndex === -1) {
      newSelectedRows.push(row);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }
    setSelectedRows(newSelectedRows);
    if (newSelectedRows?.length > 0) {
      props?.setFormObject?.(newSelectedRows[0]);
    }
  };

  const handleChangePage = async (event, value) => {
    if (onPageChange) {
      onPageChange(event, value);
    } else {
      if (lastEvaluatedKey && value === fetchedPages) {
        await handleNext();
      }
      setInternalPage(value);
    }
  };

  const onSearch = async (searchValue) => {
    setData?.([]);
    await handleNext({ searchText: searchValue });
  };

  const handleSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy],
  );
  const filteredData = useMemo(() => {
    if (!additionalFilteredStatus) return data;
    return data?.filter((row) => {
      return true;
    });
  }, [data, additionalFilteredStatus]);

  // const filteredData = useMemo(() => {
  //   return data?.filter((row) => {
  //     if (!filteredStatus && !filteredType && !additionalFilteredStatus) return true;

  //     const statusMatch = filteredStatus ? row.projectStatus === filteredStatus : true;
  //     const typeMatch = filteredType ? row.projectType === filteredType : true;

  //     return statusMatch && typeMatch;
  //   });
  // }, [data, filteredStatus, filteredType, additionalFilteredStatus, categorizedLocationsData]);

  const sortedData = useMemo(() => {
    return stableSort(filteredData || [], getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);

  useEffect(() => {
    if (data && data?.length > 0) {
      const calculatedTotalPages = isServerPagination
        ? Math.ceil(
            (totalCount !== undefined ? totalCount : data.length) / limit,
          )
        : Math.ceil(data?.length / limit);

      setTotalPages(calculatedTotalPages);

      if (isServerPagination) {
        setTableData(sortedData);
      } else {
        if (searchQuery || !lastEvaluatedKey) {
          setFetchedPages?.(calculatedTotalPages);
        }

        let validPage = page;
        if (page > calculatedTotalPages) {
          validPage = calculatedTotalPages;
          setInternalPage(validPage);
        }

        const paginatedData = sortedData?.slice(
          (validPage - 1) * limit,
          validPage * limit,
        );
        setTableData(paginatedData);
      }
    } else if (data && data?.length === 0) {
      setTotalPages(1);

      if (page > 1 && !isServerPagination) {
        setInternalPage(1);
      }
      setTableData([]);
    }
  }, [
    filteredData,
    data,
    sortedData,
    page,
    limit,
    lastEvaluatedKey,
    searchQuery,
    isServerPagination,
    totalCount,
    setFetchedPages,
  ]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          width: "100%",
          gap: header && 2,
        }}
      >
        <Header
          Icon={Icon}
          title={title}
          subtitle={subtitle}
          showAddButton={showAddButton === false ? false : hasData}
          addButtonText={addButtonText}
          showOtherButton={showOtherButton}
          otherButtonText={otherButtonText}
          onAddClick={onAddClick ? onAddClick : handleAddClick}
          onOtherClick={onOtherClick}
          showActions={showActions}
          actionOptions={actionOptions}
          selectedAction={selectedAction}
          onDeleteClick={onDeleteClick}
          handleFilterClick={handleFilterClick}
          handleFilterClose={handleFilterClose}
          openFilter={openFilter}
          openStatus={openStatus}
          openType={openType}
          anchorElFilter={anchorElFilter}
          anchorElStatus={anchorElStatus}
          anchorElType={anchorElType}
          filteredStatus={filteredStatus}
          setFilteredStatus={setFilteredStatus}
          filteredType={filteredType}
          setFilteredType={setFilteredType}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          onRefresh={onRefresh}
          columns={columns}
          openOptions={openOptions}
          anchorElOptions={anchorElOptions}
          handleOptionsClick={handleOptionsClick}
          handleOptionsClose={handleOptionsClose}
          handleAttributesClick={handleAttributesClick}
          handleSettingsClick={handleSettingsClick}
          openAttributes={openAttributes}
          anchorElAttributes={anchorElAttributes}
          handleAttributesClose={handleAttributesClose}
          handleStatusClick={handleStatusClick}
          handleStatusClose={handleStatusClose}
          handleTypeClick={handleTypeClick}
          handleTypeClose={handleTypeClose}
          visibilityStatus={visibilityStatus}
          handleToggle={handleToggle}
          selectedRows={selectedRows}
          onModifyClick={handleModifyClick}
          object={object}
          selectedObject={selectedObject}
          handleChange={handleChange}
          additionalFilterOption={additionalFilterOption}
          handleAdditionalFilterClick={handleAdditionalFilterClick}
          additionalFilteredStatus={additionalFilteredStatus}
          setAdditionalFilteredStatus={setAdditionalFilteredStatus}
          setData={setData}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              backgroundColor: "transparent",
              padding: "18px 12px",
              boxSizing: "border-box",
            }}
          >
            <SearchBarContainer
              onSearch={onSearch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Pagination
                count={
                  isServerPagination ? totalPages : (fetchedPages ?? totalPages)
                }
                page={page}
                onChange={handleChangePage}
                limit={limit}
                renderItem={(item) => {
                  if (lastEvaluatedKey && item.type === "end-ellipsis") {
                    return null;
                  }
                  if (
                    item.type === "page" &&
                    item.page === fetchedPages &&
                    lastEvaluatedKey
                  ) {
                    return <PaginationItem {...item} type="start-ellipsis" />;
                  }

                  return <PaginationItem {...item} />;
                }}
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: theme.palette.text.color,
                    border: "none",
                    minWidth: 24,
                    height: 24,
                    fontSize: "0.75rem",
                    borderRadius: 0,
                  },
                  ...(!isLoading && {
                    "& .Mui-selected": {
                      backgroundColor: "transparent !important",
                      border: "none",
                      borderBottom: "1px solid #006BCE",
                      color: "#006BCE",
                    },
                  }),
                  "& .MuiPaginationItem-previousNext": {
                    border: `1px solid ${theme.palette.border.color}`,
                    borderRadius: "50% !important",
                  },
                }}
              />
            </Box>
          </Box>

          {isPropertiesLoading ? (
            <CircularProgress
              sx={{
                color: theme.palette.text.color,
                my: 2,
              }}
            />
          ) : (
            <TableContainer
              sx={{
                width: "-webkit-fill-available",
                boxSizing: "border-box",
                backgroundColor: theme.palette.primary.main,
                borderRadius: "16px",
                border: `1px solid ${theme.palette.border.color}`,
                margin: 1,
                maxHeight: "100%",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.border.color,
                  borderRadius: "3px",
                },
              }}
            >
              <Table
                stickyHeader
                aria-label="sticky table"
                sx={{
                  paddingTop: 0,
                  backgroundColor: theme.palette.primary.color,
                  p: 1,
                  borderRadius: 2,
                  boxSizing: "border-box",
                }}
              >
                <TableHead
                  sx={{
                    "& .MuiTableRow-root": {
                      "& .MuiTableCell-root": {
                        backgroundColor: theme.palette.tableHeader.color,
                        fontWeight: "bold",
                      },
                      "& .MuiTableCell-root:first-of-type": {
                        borderTopLeftRadius: "22px",
                        borderBottomLeftRadius: "22px",
                      },
                      "& .MuiTableCell-root:last-of-type": {
                        borderTopRightRadius: "22px",
                        borderBottomRightRadius: "22px",
                      },
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      align="center"
                      padding="checkbox"
                      className={classes.headerCell}
                    >
                      {columns?.length > 0 && (
                        <Checkbox
                          color="primary"
                          indeterminate={
                            numSelected > 0 && numSelected < rowCount
                          }
                          checked={rowCount > 0 && numSelected === rowCount}
                          onChange={handleSelectAllClick}
                          sx={{
                            color: theme.palette.text.color,
                            "& .MuiSvgIcon-root": {
                              fontSize: "14px !important",
                            },
                            "&.Mui-checked": { color: "#006bce" },
                            "&.MuiCheckbox-indeterminate": { color: "#006bce" },
                          }}
                        />
                      )}
                    </TableCell>
                    {columns?.map((column) => (
                        <TableCell
                          key={column.id}
                          align="left"
                          className={classes.headerCell}
                        >
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : "asc"}
                            onClick={() => handleSort(column.id)}
                            sx={{
                              color: `${theme.palette.text.color} !important`,
                              fontSize: "14px",
                              "&:hover": {
                                color: `${theme.palette.text.color} !important`,
                              },
                              "&.Mui-active": {
                                color: `${theme.palette.text.color} !important`,
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: `${theme.palette.text.color} !important`,
                              },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <CircularProgress
                    sx={{
                      color: theme.palette.text.color,
                      my: 2,
                    }}
                  />
                ) : data?.length <= 0 ? (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{
                          py: 10,
                          borderBottom: "none",
                        }}
                      >
                        <Box sx={{ pl: 20 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 400,
                              mb: 1,
                            }}
                          >
                            {emptyText}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#888",
                              mb: 3,
                            }}
                          >
                            {emptySubtext}
                          </Typography>

                          {showAddButton !== false && (
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              sx={{
                                color: "#006bce",
                                borderColor: "#006bce",
                                textTransform: "none",
                              }}
                              onClick={() => setShowAddForm(true)}
                            >
                              {addButtonText}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {tableData?.map((row, rowIndex) => {
                      const isItemSelected = isSelected(
                        row,
                        rowIndex,
                        page,
                        limit,
                      );
                      const labelId = `enhanced-table-checkbox-${rowIndex}`;

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row[`${object}Id`]}
                          sx={{
                            "&:last-child td, &:last-child th": {
                              border: 0,
                            },
                            backgroundColor: "transparent",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                            "&:active": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <TableCell
                            align="center"
                            padding="checkbox"
                            className={classes.tableCell}
                          >
                            {columns?.length > 0 && (
                              <Checkbox
                                sx={{
                                  color: theme.palette.text.color,
                                  "&.Mui-checked": { color: "#006bce" },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: "14px !important",
                                  },
                                }}
                                checked={isItemSelected}
                                onClick={(event) =>
                                  handleSelected(event, row, rowIndex)
                                }
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            )}
                          </TableCell>
                          {columns?.map((column) => (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className={classes.tableCell}
                                sx={{
                                  cursor:
                                    column.onClick || rowClick
                                      ? "pointer"
                                      : "default",
                                  color: theme.palette.text.color,
                                }}
                                onClick={() => {
                                  column.onClick && column.onClick(row);
                                  if (rowClick) {
                                    setShowUpdateForm(true);
                                    props?.setFormObject(row);
                                  }
                                }}
                              >
                                {(() => {
                                  let colValue;
                                  if (column.id === "association:PROJECT") {
                                    colValue =
                                      row?.["association:PROJECT"]?.[0]
                                        ?.projectName || "N/A";
                                  } else if (
                                    column.id === "auditLocationStatus"
                                  ) {
                                    colValue =
                                      row?.["association:LOCATION"]?.[0]
                                        ?.locationStatus;
                                  } else if (
                                    column.id === "association:LOCATION[0]"
                                  ) {
                                    colValue =
                                      row["association:LOCATION"]?.[0]?.[
                                        `location${column.label
                                          .toLowerCase()
                                          .split(/\s+/)
                                          .map(
                                            (word) =>
                                              word.charAt(0).toUpperCase() +
                                              word.slice(1),
                                          )
                                          .join("")}`
                                      ];
                                  } else {
                                    colValue = row[column.id];
                                  }
                                  const result = RenderColumnItem(
                                    {
                                      ...column,
                                      value: colValue,
                                      coordinates:
                                        row?.imageGeoHash?.coordinates,
                                    },
                                    row,
                                    object,
                                    handleUpdateAnnotation,
                                    theme,
                                  );
                                  if (result) return result;
                                  const fallback = row[column.id];
                                  if (
                                    fallback !== null &&
                                    typeof fallback === "object"
                                  )
                                    return "N/A";
                                  return fallback;
                                })()}
                              </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NewListComponent;
